// Blood Magic: the altar, infusions, orbs (your soul network), sigils and rituals. Tier 3 and the
// late rituals (stage 8) need boss trophies.
import type { ItemId } from './items.ts';

/**
 * Altar tiers: tier 2 = all 8 tiles around the altar are blood runes; tier 3 = that, plus a blood
 * capstone on each of the four diagonal tiles two steps out (CAPSTONE_AT).
 */
export const ALTAR_TIERS = [
  { tier: 1, cap: 5000, sacrifice: 100, drain: 120 },
  { tier: 2, cap: 20000, sacrifice: 150, drain: 300 },
  { tier: 3, cap: 60000, sacrifice: 250, drain: 700 },
];
export const CAPSTONE_AT: [number, number][] = [[-2, -2], [2, -2], [-2, 2], [2, 2]];
export const SACRIFICE_HP = 10;

/** Put the input on the altar; it drinks this much life essence (LP) and becomes the output. */
export const INFUSIONS: { input: ItemId; output: ItemId; lp: number; tier: number }[] = [
  { input: 'stone', output: 'blank_slate', lp: 200, tier: 1 },
  { input: 'bloodstone', output: 'weak_blood_orb', lp: 1000, tier: 1 },
  { input: 'blank_slate', output: 'reinforced_slate', lp: 1500, tier: 2 },
  { input: 'elite_heart', output: 'apprentice_blood_orb', lp: 4000, tier: 2 },
  { input: 'tome_arcane_bolt', output: 'tome_blood_lance', lp: 3000, tier: 2 },
  { input: 'reinforced_slate', output: 'imbued_slate', lp: 5000, tier: 3 },
  { input: 'lich_phylactery', output: 'magician_blood_orb', lp: 15000, tier: 3 },
];

/** Blood orbs: holding one binds you to a soul network of this size. */
export const ORBS: Partial<Record<ItemId, number>> = { weak_blood_orb: 5000, apprentice_blood_orb: 25000, magician_blood_orb: 100000 };

export type SigilId = 'mending' | 'swiftness' | 'growth' | 'blood_bolt' | 'blood_lamp';

export const SIGILS: Record<SigilId, { item: ItemId; lp: number; cooldown: number; aim?: boolean; text: string }> = {
  blood_bolt: { item: 'sigil_blood_bolt', lp: 40, cooldown: 0.6, aim: true, text: 'Right-click where to fire a bolt of blood (22 damage).' },
  mending: { item: 'sigil_mending', lp: 150, cooldown: 4, text: 'Right-click to heal 35 health.' },
  swiftness: { item: 'sigil_swiftness', lp: 150, cooldown: 2, text: 'Right-click to be Swift for 60 seconds.' },
  growth: { item: 'sigil_growth', lp: 300, cooldown: 2, text: 'Right-click crops to grow everything nearby one stage.' },
  blood_lamp: { item: 'sigil_blood_lamp', lp: 20, cooldown: 0.5, text: 'Right-click a tile to light a blood lamp there.' },
};

export const BOLT = { speed: 560, damage: 22, range: 9 * 48 };

/** Ritual of the Green Grove: crops within this many tiles grow twice as fast while it drinks LP. */
export const GROVE = { radius: 4, startLp: 1000, lpPerTick: 15, tickSecs: 5 };

/**
 * Late rituals: right-click a master ritual stone with a focus to change which ritual it works
 * (the focus is used up; a grove focus switches it back).
 * Warding Veil: banishes monsters that come within `radius` and stops wild ones appearing there;
 * a little LP per tick to keep up, more per monster banished.
 * Well of Suffering: hurts monsters within `radius` every tick and pours LP into the nearest altar.
 */
export type RitualId = 'grove' | 'ward' | 'well';
export const WARD = { radius: 10, startLp: 3000, lpPerTick: 4, lpPerBanish: 50, tickSecs: 5 };
export const WELL = { radius: 6, startLp: 2000, lpPerTick: 10, dmg: 8, lpPerHit: 60, altarRange: 10, tickSecs: 3 };
export const RITUALS: Record<RitualId, { name: string; startLp: number; text: string }> = {
  grove: { name: 'the Green Grove', startLp: GROVE.startLp, text: `crops within ${GROVE.radius} tiles grow twice as fast` },
  ward: { name: 'the Warding Veil', startLp: WARD.startLp, text: `monsters can't come within ${WARD.radius} tiles` },
  well: { name: 'the Well of Suffering', startLp: WELL.startLp, text: `monsters within ${WELL.radius} tiles bleed into your nearest altar` },
};
export const FOCUS: Partial<Record<ItemId, RitualId>> = { focus_warding: 'ward', focus_suffering: 'well', focus_grove: 'grove' };
